import { OptionPricer, INT256_MAX } from './option-pricer';
import BigNumber from 'bignumber.js';
import { SpotPricer } from './spot-pricer';
import { Ledger } from './ledger';
import { Config } from './config';
import { OptionMarket } from './option-market';
import { UNIT } from 'src/app/prototype/big-number';
import { ZeroAddress } from 'src/app/services/eth-wallet.service';

interface PositionParams {
  expiry: number;
  strike: BigNumber;
  isCall: boolean;
  size: BigNumber;
  notional: BigNumber;
}

interface TxCache {
  now: number;
  spot: BigNumber;
  riskFreeRate: BigNumber;
  initialMarginRiskRate: BigNumber;
  spotInitialMarginRiskRate: BigNumber;
  priceRatio: BigNumber;
  priceRatio2: BigNumber;
  priceRatioUtilization: BigNumber;
  spotFee: BigNumber;
  optionFee: BigNumber;
  minPremium: BigNumber;
  exerciseFeeRate: BigNumber;
  profitFeeRate: BigNumber;
  poolLength: number;
  pools: string[];
  loaded: boolean[];
  available: BigNumber[];
  adjustedAvailable: BigNumber[];
  equity: BigNumber[];
}

interface TradingPoolsInfo {
  totalEquity: BigNumber;
  totalAvailable: BigNumber;
  totalAdjustedAvailable: BigNumber;
  totalSize: BigNumber;
  rates: BigNumber[];
  indexes: number[];
  length: number;
  isClose: boolean;
}
interface PositionInfo {
  // int buySize;
  buyNotional: BigNumber;
  buyValue: BigNumber;
  unsettledSellSize: BigNumber;
  sellNotional: BigNumber;
  sellValue: BigNumber;
  settledRisk: BigNumber;
  fee: BigNumber;
}

export interface AccountInfo {
  initialMargin: BigNumber;
  marginBalance: BigNumber;
  equity: BigNumber;
  equityWithFee: BigNumber;
  upnl: BigNumber;
  available: BigNumber;
  healthFactor: BigNumber;
}


export class Vault {
  config = new Config();
  optionPricer = new OptionPricer();
  spotPricer = new SpotPricer();
  ledger = new Ledger();
  optionMarket = new OptionMarket();
  private accountInfoCache: any = {};

  getAccountInfo(account: string) {
    return this.internalGetAccountInfo(this.initTxCache(), account);
  }

  getPremium(expiry: number, strike: BigNumber, isCall: boolean, size: BigNumber) {
    const txCache: TxCache = this.initTxCache();
    if (txCache.now >= expiry) {
      const settledPrice = this.spotPricer.getSettledPrice(expiry);
      return this.getTradeTypeSettledValue(txCache.exerciseFeeRate, txCache.profitFeeRate, strike, settledPrice.eq(0) ? txCache.spot : settledPrice, isCall, size.times(-1));
    } else {
      const positionParams: PositionParams = { expiry, strike, isCall, size, notional: new BigNumber(0) };
      let tradingPoolsInfo = this.getTradingPoolsInfo(txCache, positionParams, true, ZeroAddress);
      let closePremium = new BigNumber(0);
      let closeFee = new BigNumber(0);
      let remainSize = size;
      if (tradingPoolsInfo.totalSize.abs().lt(size.abs())) {
        if (!tradingPoolsInfo.totalSize.eq(0)) {
          [closePremium, closeFee] = this.internalGetPremium(txCache, expiry, strike, isCall, tradingPoolsInfo.totalSize, INT256_MAX, new BigNumber(0));
          remainSize = remainSize.minus(tradingPoolsInfo.totalSize);
        }
        tradingPoolsInfo = this.getTradingPoolsInfo(txCache, positionParams, false, ZeroAddress);
        tradingPoolsInfo.totalAvailable = tradingPoolsInfo.totalAvailable.minus(closePremium.plus(closeFee));
        tradingPoolsInfo.totalEquity = tradingPoolsInfo.totalEquity.minus(closeFee);
      }
      const [premium, fee] = this.internalGetPremium(txCache, expiry, strike, isCall, remainSize, tradingPoolsInfo.isClose ? INT256_MAX : tradingPoolsInfo.totalAvailable, tradingPoolsInfo.totalEquity);
      return [premium.plus(closePremium), fee.plus(closeFee)];
    }
  }

  private internalGetAccountInfo(txCache: TxCache, account: string) {
    const cache = this.accountInfoCache[account];
    const timestamp = txCache.now - txCache.now % 5;
    const state =`${timestamp}_${txCache.spot.toString(10)}`;
    if (!cache || state !== cache.state) {
      this.accountInfoCache[account] = {
        value: this.internalGetAccountInfoWithPositionInfo(this.getPositionInfo(txCache, account), txCache, account),
        state
      };
    }
    return this.accountInfoCache[account].value;
  }

  private getPositionInitialMargin(positionInfo: PositionInfo, spot: BigNumber, initialMarginRiskRate: BigNumber) {
    return positionInfo.unsettledSellSize.times(-1).decimalMul(spot).plus(positionInfo.settledRisk).decimalMul(initialMarginRiskRate).plus(positionInfo.sellValue.times(-1));
  }

  private internalGetAccountInfoWithPositionInfo(positionInfo: PositionInfo, txCache: TxCache, account: string) {
    const accountInfo: AccountInfo = {} as any;
    accountInfo.marginBalance = this.ledger.balanceOf(account).plus(positionInfo.buyNotional).plus(positionInfo.sellNotional);
    accountInfo.equity = accountInfo.marginBalance.plus(positionInfo.buyValue).plus(positionInfo.sellValue);
    accountInfo.equityWithFee = accountInfo.equity.plus(positionInfo.fee);
    accountInfo.initialMargin = this.getPositionInitialMargin(positionInfo, txCache.spot, txCache.initialMarginRiskRate);
    accountInfo.upnl = positionInfo.buyNotional.plus(positionInfo.buyValue).plus( positionInfo.sellNotional).plus(positionInfo.sellValue);
    accountInfo.available = accountInfo.marginBalance.minus(accountInfo.initialMargin);

    const riskDenominator = accountInfo.initialMargin.plus(positionInfo.buyValue).plus(positionInfo.sellValue);
    accountInfo.healthFactor = riskDenominator.eq(0) ? INT256_MAX : accountInfo.equity.decimalDiv(riskDenominator);
    return accountInfo;
  }

  private getPositionInfo(txCache: TxCache, account: string) {
    const expiries = this.ledger.listOfExpiries(account);
    const positionParams: PositionParams = {} as any;
    const positionInfo: PositionInfo = {
      buyNotional: new BigNumber(0),
      buyValue: new BigNumber(0),
      unsettledSellSize: new BigNumber(0),
      sellNotional: new BigNumber(0),
      sellValue: new BigNumber(0),
      settledRisk: new BigNumber(0),
      fee: new BigNumber(0)
    };
    for (let i = 0; i < expiries.length; ++i) {
      positionParams.expiry = expiries[i];
      const settledPrice = txCache.now >= positionParams.expiry ? this.spotPricer.getSettledPrice(positionParams.expiry) : new BigNumber(0);
      const strikes = this.ledger.listOfStrikes(account, positionParams.expiry);
      for (let j = 0; j < strikes.length; ++j) {
        positionParams.strike = strikes[j];
        this.getPositionInfoSub(txCache, account, settledPrice, positionParams, positionInfo, true);
        this.getPositionInfoSub(txCache, account, settledPrice, positionParams, positionInfo, false);
      }
    }
    return positionInfo;
  }

  private getPositionInfoSub(txCache: TxCache, account: string, settledPrice: BigNumber, positionParams: PositionParams, positionInfo: PositionInfo, isCall: boolean) {
    const size = this.ledger.positionSizeOf(account, positionParams.expiry, positionParams.strike, isCall);
    if (size.eq(0)) {
      return;
    }
    positionParams.size = size;
    positionParams.isCall = isCall;
    const [notional, value, fee] = this.getTradeTypeInfo(txCache, account, positionParams, settledPrice);
    positionInfo.fee = positionInfo.fee.plus(fee);
    if (size.gt(0)) {
      positionInfo.buyNotional = positionInfo.buyNotional.plus(notional);
      positionInfo.buyValue = positionInfo.buyValue.plus(value);
    } else {
      if (settledPrice.eq(0)) {
        positionInfo.unsettledSellSize = positionInfo.unsettledSellSize.plus(size);
      } else {
        positionInfo.settledRisk = positionInfo.settledRisk.plus(size.times(-1)).decimalMul(settledPrice);
      }
      positionInfo.sellNotional = positionInfo.sellNotional.plus(notional);
      positionInfo.sellValue = positionInfo.sellValue.plus(value);
    }
  }

  private getTradeTypeInfo(txCache: TxCache, account: string, positionParams: PositionParams, settledPrice: BigNumber) {
    let notional = this.ledger.accountPositions[account][positionParams.expiry][positionParams.strike.toString(10)][positionParams.isCall.toString()].notional;
    let [value, fee] = txCache.now >= positionParams.expiry ?
      this.getTradeTypeSettledValue(txCache.exerciseFeeRate, txCache.profitFeeRate, positionParams.strike, settledPrice.eq(0) ? txCache.spot :
      settledPrice, positionParams.isCall, positionParams.size) : this.internalGetPremium(txCache, positionParams.expiry, positionParams.strike, positionParams.isCall, positionParams.size.times(-1), INT256_MAX, new BigNumber(0));
    return [notional, value, fee] ;
  }

  private getTradeTypeSettledValue(exerciseFeeRate: BigNumber, profitFeeRate: BigNumber, strike: BigNumber, settledPrice: BigNumber, isCall: boolean, size: BigNumber) {
    let value = new BigNumber(0);
    let fee = new BigNumber(0);
    if (isCall) {
      if (settledPrice.gt(strike)) {
        value = settledPrice.minus(strike).decimalMul(size);
      }
    } else {
      if (settledPrice < strike) {
        value = strike.minus(settledPrice).decimalMul(size);
      }
    }
    if (!value.eq(0)) {
      fee = settledPrice.decimalMul(exerciseFeeRate);
      let fee2 = value.abs().decimalMul(profitFeeRate);
      if (fee2.lt(fee)) {
        fee = fee2;
      }
      fee = fee.times(-1);
    }
    return [value, fee];
  }

  private internalGetPremium(txCache: TxCache, expiry: number, strike: BigNumber, isCall: boolean, size: BigNumber, available: BigNumber, equity: BigNumber) {
    return this.optionPricer.getPremium({
      now: txCache.now,
      spot: txCache.spot,
      riskFreeRate: txCache.riskFreeRate,
      initialMarginRiskRate: txCache.initialMarginRiskRate,
      spotFee: txCache.spotFee,
      optionFee: txCache.optionFee,
      minPremium: txCache.minPremium,
      expiry,
      strike,
      iv: this.optionMarket.getIv(expiry, strike, isCall, size.gt(0)),
      size,
      available,
      equity,
      priceRatio: txCache.priceRatio,
      priceRatio2: txCache.priceRatio2,
      priceRatioUtilization: txCache.priceRatioUtilization,
      isCall
    });
  }

  private initTxCache() {
    const txCache: TxCache = {} as any;
    const pools = this.config.getPools();
    txCache.poolLength = this.config.pools.length;
    txCache.spot = this.spotPricer.getPrice();
    txCache.initialMarginRiskRate = this.config.initialMarginRiskRate;
    txCache.spotInitialMarginRiskRate = txCache.spot.decimalMul(txCache.initialMarginRiskRate);
    txCache.priceRatio = this.config.priceRatio;
    txCache.priceRatio2 = this.config.priceRatio2;
    txCache.priceRatioUtilization = this.config.priceRatioUtilization;
    txCache.spotFee = this.config.spotFee;
    txCache.optionFee = this.config.optionFee;
    txCache.minPremium = this.config.minPremium;
    txCache.exerciseFeeRate = this.config.exerciseFeeRate;
    txCache.profitFeeRate = this.config.profitFeeRate;
    txCache.now = Math.floor(Date.now() / 1000);
    txCache.riskFreeRate = this.config.riskFreeRate;
    txCache.pools = [];
    txCache.loaded = [];
    txCache.available = [];
    txCache.adjustedAvailable = [];
    txCache.equity = [];
    for (let i = 0; i < txCache.poolLength; ++i) {
      txCache.pools[i] = pools[i];
      txCache.loaded[i] = false;
      txCache.available[i] = new BigNumber(0);
      txCache.adjustedAvailable[i] = new BigNumber(0);
      txCache.equity[i] = new BigNumber(0);
    }
    return txCache;
  }

  private getTradingPoolsInfo(txCache: TxCache, positionParams: PositionParams, isClose: boolean, excludedPool: string) {
    const tradingPoolsInfo: TradingPoolsInfo = {
      totalEquity: new BigNumber(0),
      totalAvailable: new BigNumber(0),
      totalAdjustedAvailable: new BigNumber(0),
      totalSize: new BigNumber(0),
      rates: [],
      indexes: [],
      length: 0,
      isClose: false
    } as any;
    const isBuy = positionParams.size.gt(0);
    tradingPoolsInfo.isClose = isClose;
    for (let i = 0; i < txCache.poolLength; ++i) {
      tradingPoolsInfo.rates[i] = new BigNumber(0);
      const pool = txCache.pools[i];
      if (excludedPool == pool) {
        continue;
      }

      if (isClose) {
        const size = this.ledger.positionSizeOf(pool, positionParams.expiry, positionParams.strike, positionParams.isCall);
        if (!(isBuy && size.gt(0) || !isBuy && size.lt(0))) {
          continue;
        }
        tradingPoolsInfo.rates[i] = size;
        tradingPoolsInfo.totalSize = tradingPoolsInfo.totalSize.plus(size);
      } else {
        if (!txCache.loaded[i]) {
          const accountInfo = this.internalGetAccountInfo(txCache, pool);
          const [paused, reservedRate] = this.config.getPoolReservedRateForTrade(txCache.pools[i]);
          if (!paused) {
            txCache.available[i] = accountInfo.available;
            txCache.adjustedAvailable[i] = accountInfo.available.minus(accountInfo.marginBalance.decimalMul(reservedRate));
            txCache.equity[i] = accountInfo.equity;
          }
          txCache.loaded[i] = true;
        }
        if (txCache.adjustedAvailable[i].gt(0)) {
          tradingPoolsInfo.rates[i] = txCache.adjustedAvailable[i];
          tradingPoolsInfo.totalAvailable = tradingPoolsInfo.totalAvailable.plus(txCache.available[i]);
          tradingPoolsInfo.totalAdjustedAvailable = tradingPoolsInfo.totalAdjustedAvailable.plus(txCache.adjustedAvailable[i]);
        }
        tradingPoolsInfo.totalEquity = tradingPoolsInfo.totalEquity.plus(txCache.equity[i]);
      }
      if (!tradingPoolsInfo.rates[i].eq(0)) {
        tradingPoolsInfo.indexes[tradingPoolsInfo.length++] = i;
      }
    }

    if (tradingPoolsInfo.length > 0) {
      let remaining = UNIT;
      const base = isClose ? tradingPoolsInfo.totalSize : tradingPoolsInfo.totalAdjustedAvailable;
      let index = 0;
      for (let i = 0; i < tradingPoolsInfo.length - 1; ++i) {
        index = tradingPoolsInfo.indexes[i];
        tradingPoolsInfo.rates[index] = tradingPoolsInfo.rates[index].decimalDivRound(base);
        remaining = remaining.minus(tradingPoolsInfo.rates[index]);
      }
      index = tradingPoolsInfo.indexes[tradingPoolsInfo.length - 1];
      tradingPoolsInfo.rates[index] = remaining;
    }
    return tradingPoolsInfo;
  }
}
